import { useState, useCallback } from 'react';
import { FlightInfo, FlightGroup, Quotation } from '@/components/quotation/types';
import { parseISO } from 'date-fns';

// Extended interface for properties not in the base FlightInfo
export interface ExtendedFlightInfo extends FlightInfo {
  pageId?: string;
  pageTitle?: string;
}

// Type for connection used locally to fix TypeScript errors
export type Connection = {
  id: string;
  airline: string;
  flightNumber: string;
  departureAirport: string;
  departureDateTime: string;
  arrivalAirport: string;
  arrivalDateTime: string;
  flightDuration: string;
  arrivalTerminal?: string;
  departureTerminal?: string;
  waitingTime?: string;
};

export interface FlightsByGroup {
  [groupId: string]: {
    groupTitle: string;
    groupDescription?: string;
    flights: ExtendedFlightInfo[];
  };
}

export function useFlightsState(quotation: Quotation) {
  // Group flights based on grouping mode
  const flightsByGroup: FlightsByGroup = {};
  
  // Helper to find flight across all pages
  const findFlightById = (id: string): ExtendedFlightInfo | undefined => {
    for (const page of quotation.pages) {
      const found = page.flights?.find(flight => flight.id === id);
      if (found) {
        return {
          ...found,
          pageId: page.id,
          pageTitle: page.title || `Página ${page.id}`
        };
      }
    }
    return undefined;
  };
  
  // Check if we should use custom groups
  const useCustomGroups = quotation.useCustomFlightGroups && quotation.flightGroups && quotation.flightGroups.length > 0;
  
  if (useCustomGroups) {
    // Sort groups by order
    const sortedGroups = [...(quotation.flightGroups || [])].sort((a, b) => a.order - b.order);
    
    // Create flights grouping based on custom groups
    sortedGroups.forEach(group => {
      const groupFlights: ExtendedFlightInfo[] = [];
      
      // Get flights for this group
      group.flightIds.forEach(flightId => {
        const flight = findFlightById(flightId);
        if (flight) {
          groupFlights.push(flight);
        }
      });
      
      if (groupFlights.length > 0) {
        flightsByGroup[group.id] = {
          groupTitle: group.name,
          groupDescription: group.description,
          flights: groupFlights
        };
      }
    });
  } else {
    // Default grouping by page
    quotation.pages.forEach((page) => {
      if (page.flights && page.flights.length > 0) {
        flightsByGroup[page.id] = {
          groupTitle: page.title || `Página ${page.id}`,
          flights: page.flights.map(flight => ({
            ...flight,
            pageId: page.id,
            pageTitle: page.title || `Página ${page.id}`
          }))
        };
      }
    });
  }
  
  // Check if there are any flights
  const hasFlights = Object.keys(flightsByGroup).length > 0;
  
  // State to track expanded flights for details
  const [expandedFlights, setExpandedFlights] = useState<Record<string, boolean>>({});
  
  // State to track collapsed page sections - initializing with true for all
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>(() => {
    // Create an object with true (collapsed) for all groupIds
    const initialState: Record<string, boolean> = {};
    Object.keys(flightsByGroup).forEach(groupId => {
      initialState[groupId] = true; // Start with collapsed sections
    });
    return initialState;
  });

  // Function to calculate total flight duration
  const calculateTotalFlightTime = (flight: FlightInfo): string => {
    if (!flight) return '0m';
    
    let totalMinutes = 0;
    
    // If main flight has flightDuration, use it, otherwise calculate from timestamps
    if (flight.flightDuration) {
      totalMinutes = parseDurationToMinutes(flight.flightDuration);
    } else if (flight.departureDateTime && flight.arrivalDateTime) {
      totalMinutes = calculateMinutesBetweenISOStrings(
        flight.departureDateTime,
        flight.arrivalDateTime
      );
    }
    
    // Add each connection's duration and waiting time
    if (flight.connections && flight.connections.length > 0) {
      flight.connections.forEach((connection, index) => {
        // Add connection flight duration
        if (connection.flightDuration) {
          totalMinutes += parseDurationToMinutes(connection.flightDuration);
        } else if (connection.departureDateTime && connection.arrivalDateTime) {
          totalMinutes += calculateMinutesBetweenISOStrings(
            connection.departureDateTime,
            connection.arrivalDateTime
          );
        }
        
        // Add waiting time before this connection
        if (connection.waitingTime) {
          // Use explicit waiting time if available
          totalMinutes += parseDurationToMinutes(connection.waitingTime);
        } else if (index === 0) {
          // Waiting time between main flight and first connection
          if (flight.arrivalDateTime && connection.departureDateTime) {
            const waitingTime = calculateMinutesBetweenISOStrings(
              flight.arrivalDateTime,
              connection.departureDateTime
            );
            totalMinutes += waitingTime;
          }
        } else if (index > 0 && flight.connections[index - 1].arrivalDateTime && connection.departureDateTime) {
          // Waiting time between consecutive connections
          const waitingTime = calculateMinutesBetweenISOStrings(
            flight.connections[index - 1].arrivalDateTime,
            connection.departureDateTime
          );
          totalMinutes += waitingTime;
        }
      });
    }
    
    // Format using hours and minutes
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    return `${hours}h ${minutes}min`;
  };
  
  // Helper function to calculate minutes between ISO strings
  const calculateMinutesBetweenISOStrings = (start: string, end: string): number => {
    if (!start || !end) return 0;
    
    try {
      // Use parseISO instead of direct Date creation to avoid timezone issues
      const startDate = parseISO(start);
      const endDate = parseISO(end);
      
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return 0;
      }
      
      // Calculate the difference and ensure it's never negative
      const diffInMinutes = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60));
      return Math.max(0, diffInMinutes); // Return 0 if the difference is negative
    } catch (error) {
      console.error("Erro ao calcular duração:", error);
      return 0;
    }
  };
  
  // Helper function to parse duration string into minutes
  const parseDurationToMinutes = (duration: string): number => {
    if (!duration) return 0;
    
    try {
      // Handle "HH:MM" format
      if (duration.includes(':')) {
        const [hours, minutes] = duration.split(':').map(part => parseInt(part, 10));
        return (hours || 0) * 60 + (minutes || 0);
      }
      
      // Handle "Xh Ymin" or "Xh Ym" format
      const hoursMatch = duration.match(/(\d+)\s*h/);
      const minutesMatch = duration.match(/(\d+)\s*min/);
      const minsMatch = duration.match(/(\d+)\s*m/);
      
      const hours = hoursMatch ? parseInt(hoursMatch[1], 10) : 0;
      const minutes = minutesMatch ? parseInt(minutesMatch[1], 10) : (minsMatch ? parseInt(minsMatch[1], 10) : 0);
      
      return hours * 60 + minutes;
    } catch (error) {
      console.error("Error parsing duration:", error);
      return 0;
    }
  };
  
  // Helper function to calculate wait time between connections
  const calculateWaitTime = (currentConnection: Connection, nextConnection: Connection): string => {
    if (!currentConnection.arrivalDateTime || !nextConnection.departureDateTime) {
      return 'Não disponível';
    }
    
    try {
      // Use parseISO instead of direct Date creation to avoid timezone issues
      const arrivalTime = parseISO(currentConnection.arrivalDateTime);
      const departureTime = parseISO(nextConnection.departureDateTime);
      
      const waitTimeMs = departureTime.getTime() - arrivalTime.getTime();
      
      if (waitTimeMs < 0) return 'Não disponível';
      
      const waitTimeMinutes = Math.round(waitTimeMs / (1000 * 60));
      const hours = Math.floor(waitTimeMinutes / 60);
      const minutes = waitTimeMinutes % 60;
      
      return `${hours}h ${minutes}m`;
    } catch (e) {
      return 'Não disponível';
    }
  };

  // Toggle flight details expansion
  const toggleFlightDetails = useCallback((flightId: string) => {
    setExpandedFlights(prev => ({
      ...prev,
      [flightId]: !prev[flightId]
    }));
  }, []);

  // Toggle collapsed state for a section
  const toggleSection = useCallback((groupId: string) => {
    setCollapsedSections(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  }, []);

  return {
    flightsByGroup,
    hasFlights,
    expandedFlights,
    collapsedSections,
    toggleFlightDetails,
    toggleSection,
    useCustomGroups,
    calculateTotalFlightTime,
    calculateWaitTime
  };
} 